import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  birthdate: "",
  gender: "",
  address: "",
  notes: "",
};

export default function PatientDialog({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        name: editing.name || "",
        email: editing.email || "",
        phone: editing.phone || "",
        birthdate: editing.birthdate || "",
        gender: editing.gender || "",
        address: editing.address || "",
        notes: editing.notes || "",
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, editing]);

  const handleSubmit = async () => {
    if (!form.name) {
      toast.error("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/patients/${editing.id}`, form);
        toast.success("Paciente actualizado");
      } else {
        await api.post("/patients", form);
        toast.success("Paciente creado");
      }
      onSaved && (await onSaved());
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-white" data-testid="patient-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-[400] text-[#3A2418]">
            {editing ? "Editar clienta" : "Nueva clienta"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-sm">Nombre completo *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ej: María González"
              className="mt-1.5 bg-white border-[#E8DFD2]"
              data-testid="patient-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 bg-white border-[#E8DFD2]"
                data-testid="patient-email-input"
              />
            </div>
            <div>
              <Label className="text-sm">Teléfono</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+52 55 1234 5678"
                className="mt-1.5 bg-white border-[#E8DFD2]"
                data-testid="patient-phone-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Fecha de nacimiento</Label>
              <Input
                type="date"
                value={form.birthdate}
                onChange={(e) => setForm({ ...form, birthdate: e.target.value })}
                className="mt-1.5 bg-white border-[#E8DFD2]"
                data-testid="patient-birthdate-input"
              />
            </div>
            <div>
              <Label className="text-sm">Género</Label>
              <Select value={form.gender || undefined} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger className="mt-1.5 bg-white border-[#E8DFD2]" data-testid="patient-gender-select">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Masculino">Masculino</SelectItem>
                  <SelectItem value="Femenino">Femenino</SelectItem>
                  <SelectItem value="Otro">Otro</SelectItem>
                  <SelectItem value="Prefiero no decir">Prefiero no decir</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-sm">Dirección</Label>
            <Input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="mt-1.5 bg-white border-[#E8DFD2]"
              data-testid="patient-address-input"
            />
          </div>
          <div>
            <Label className="text-sm">Notas internas</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="mt-1.5 bg-white border-[#E8DFD2]"
              data-testid="patient-notes-input"
            />
          </div>
        </div>
        <DialogFooter className="mt-6">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-[#B5704A] hover:bg-[#9C5E3D] hover:text-white text-white rounded-full" data-testid="patient-save-btn">
            {saving ? "Guardando..." : editing ? "Actualizar" : "Crear clienta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
